// Shopify consent owns this integration. No SDK, storage or events before consent.
const EVENT_NAMES = new Set(['$pageview', '$autocapture', 'ticket_selected', 'compare_tickets_opened', 'testimonial_opened', 'checkout_clicked', 'ticket_added_to_cart']);
const TICKETS = new Set(['conference', 'vip', 'mastermind']);
const SAFE_PROPERTIES = new Set(['token', 'distinct_id', '$device_id', '$session_id', '$window_id', '$lib', '$lib_version', '$browser', '$browser_version', '$os', '$os_version', '$device_type', '$screen_height', '$screen_width', '$viewport_height', '$viewport_width', '$time', '$insert_id']);
const BLOCKED_ELEMENT = 'form, input, textarea, select, [contenteditable], .ph-no-capture, .ph-no-autocapture, [data-ph-no-autocapture], [data-sensitive]';

export function trackableLocation(hostWindow) {
  try {
    const url = new URL(hostWindow.location.href);
    const path = decodeURIComponent(url.pathname).toLowerCase();
    if (!['themasterminders.com', 'www.themasterminders.com', 'h19enn-ad.myshopify.com'].includes(url.hostname) || url.protocol !== 'https:') return false;
    if (hostWindow.Shopify?.designMode || (hostWindow.Shopify?.theme?.role && hostWindow.Shopify.theme.role !== 'main')) return false;
    if ([...url.searchParams.keys()].some(key => /^(preview|preview_theme_id|preview_key|previewpath|view|pb|_fd|_ab|shopify-preview)$/i.test(key))) return false;
    if (/(?:^|\/)(?:accounts?|customers?|checkouts?|orders?|apps|password|challenge|search)(?:\/|$)/i.test(path)) return false;
    if (/\/cart\/.+/.test(path)) return false;
    return true;
  } catch { return false; }
}

function ticketName(value) {
  const ticket = value === 'standard' ? 'conference' : value;
  return TICKETS.has(ticket) ? ticket : undefined;
}

function eventProperties(name, source = {}) {
  const properties = {};
  const ticket = ticketName(source.ticket_type);
  if (ticket && ['ticket_selected', 'ticket_added_to_cart'].includes(name)) properties.ticket_type = ticket;
  if (name === 'ticket_added_to_cart') {
    if (/^\d{1,20}$/.test(String(source.variant_id))) properties.variant_id = String(source.variant_id);
    if (Number.isSafeInteger(source.quantity) && source.quantity > 0 && source.quantity <= 100) properties.quantity = source.quantity;
    if (Number.isFinite(source.value) && source.value >= 0 && source.value <= 1000000) properties.value = source.value;
    if (typeof source.currency === 'string' && /^[A-Z]{3}$/.test(source.currency)) properties.currency = source.currency;
  }
  if (name === 'testimonial_opened' && /^[A-Za-z0-9_-]{11}$/.test(source.video_id)) properties.video_id = source.video_id;
  return properties;
}

export function sanitiseEvent(event, hostWindow, allowed) {
  if (!allowed() || !trackableLocation(hostWindow) || !EVENT_NAMES.has(event?.event)) return null;
  const source = event.properties || {};
  const properties = eventProperties(event.event, source);
  for (const key of SAFE_PROPERTIES) {
    const value = source[key];
    if (typeof value === 'number' && Number.isFinite(value)) properties[key] = value;
    else if (typeof value === 'string' && value.length <= 150 && !/[?@#\r\n]/.test(value)) properties[key] = value;
  }
  const url = new URL(hostWindow.location.href);
  properties.$current_url = `${url.origin}${url.pathname}`;
  properties.$pathname = url.pathname;
  properties.$host = url.host;
  properties.$process_person_profile = false;
  // Keep only structural element positions; never labels, attributes or entered text.
  if (event.event === '$autocapture') {
    if (source.$event_type !== 'click') return null;
    properties.$event_type = 'click';
    if (Array.isArray(source.$elements)) properties.$elements = source.$elements.slice(0, 12).map(element => {
      const safe = {};
      if (/^[a-z]{1,20}$/.test(element.tag_name)) safe.tag_name = element.tag_name;
      for (const key of ['nth_child', 'nth_of_type']) if (Number.isSafeInteger(element[key]) && element[key] >= 0) safe[key] = element[key];
      return safe;
    });
    // Match the SDK's chain grammar using only our sanitised structural fields.
    if (properties.$elements) properties.$elements_chain = properties.$elements
      .filter(element => element.tag_name)
      .map(element => `${element.tag_name}:nth-child="${element.nth_child || 0}"nth-of-type="${element.nth_of_type || 0}"`)
      .join(';');
  }
  return { ...event, properties };
}

export function setupPostHog(config, hostWindow = window, doc = document) {
  if (hostWindow.mmAnalytics) return hostWindow.mmAnalytics;
  let client = null;
  let loading = false;
  let scriptLoaded = false;
  let initialising = false;
  let failed = false;
  let optedIn = false;
  let pageviewSent = false;
  const configured = config?.enabled === true && /^phc_[A-Za-z0-9_-]{8,200}$/.test(config.projectToken || '') && ['eu', 'us'].includes(config.region);
  const allowed = () => {
    try {
      return configured && trackableLocation(hostWindow) && hostWindow.navigator?.doNotTrack !== '1' && hostWindow.Shopify?.customerPrivacy?.analyticsProcessingAllowed() === true;
    } catch { return false; }
  };
  function capture(name, properties = {}) {
    try {
      if (!client || !optedIn || !allowed() || !EVENT_NAMES.has(name) || name.startsWith('$')) return false;
      client.capture(name, eventProperties(name, properties), { send_instantly: true, transport: 'sendBeacon' });
      return true;
    } catch { return false; }
  }
  const api = Object.freeze({ capture });
  hostWindow.mmAnalytics = api;
  if (!configured || !trackableLocation(hostWindow) || hostWindow.posthog) return api;

  function syncConsent() {
    try {
      if (!allowed()) {
        if (client) client.opt_out_capturing();
        optedIn = false;
        return;
      }
      if (failed) return;
      if (!client) {
        if (scriptLoaded) initialise();
        else if (!loading) loadSdk();
        return;
      }
      if (!optedIn) {
        client.opt_in_capturing({ captureEventName: false });
        optedIn = true;
      }
      if (!pageviewSent) {
        pageviewSent = true;
        client.capture('$pageview');
      }
    } catch { optedIn = false; }
  }

  function initialise() {
    if (initialising || client || !allowed()) return;
    initialising = true;
    try {
      hostWindow.posthog.init(config.projectToken, {
        api_host: `https://${config.region}.i.posthog.com`,
        ui_host: `https://${config.region}.posthog.com`,
        person_profiles: 'never',
        persistence_name: 'mm_posthog',
        persistence: 'localStorage',
        opt_out_capturing_by_default: true,
        opt_out_persistence_by_default: true,
        respect_dnt: true,
        capture_pageview: false,
        capture_pageleave: false,
        disable_capture_url_hashes: true,
        autocapture: {
          dom_event_allowlist: ['click'],
          element_allowlist: ['a', 'button'],
          css_selector_allowlist: ['.mmd a[href]', '.mmd button', '.mm a[href]', '.mm button', 'header a[href]', 'header button', 'nav a[href]', 'nav button', 'footer a[href]', 'footer button'],
          css_selector_ignorelist: BLOCKED_ELEMENT.split(', '),
          capture_copied_text: false,
        },
        mask_all_text: true,
        mask_all_element_attributes: true,
        disable_session_recording: true,
        session_recording: { maskAllInputs: true, maskTextSelector: '*', blockSelector: 'form' },
        disable_external_dependency_loading: true,
        disable_surveys: true,
        disable_web_experiments: true,
        advanced_disable_flags: true,
        capture_heatmaps: false,
        capture_dead_clicks: false,
        capture_performance: false,
        capture_exceptions: false,
        rageclick: false,
        save_referrer: false,
        save_campaign_params: false,
        request_batching: false,
        before_send: event => sanitiseEvent(event, hostWindow, allowed),
        loaded(instance) {
          client = instance;
          syncConsent();
        },
      });
    } catch { failed = true; }
  }

  function loadSdk() {
    loading = true;
    const script = doc.createElement('script');
    script.async = true;
    script.crossOrigin = 'anonymous';
    script.referrerPolicy = 'no-referrer';
    script.src = `https://${config.region}-assets.i.posthog.com/static/array.js`;
    script.onload = () => {
      loading = false;
      scriptLoaded = typeof hostWindow.posthog?.init === 'function';
      if (!scriptLoaded) failed = true;
      syncConsent();
    };
    script.onerror = () => { loading = false; failed = true; };
    doc.head.appendChild(script);
  }

  doc.addEventListener('visitorConsentCollected', syncConsent);
  doc.addEventListener('click', event => {
    if (!allowed()) return;
    const target = event.target?.closest?.('a[href], button');
    if (!target || target.disabled || target.getAttribute('aria-disabled') === 'true' || target.closest(BLOCKED_ELEMENT)) return;
    const video = target.dataset.mmWatch;
    if (video) return void capture('testimonial_opened', { video_id: video });
    const href = target.getAttribute('href');
    if (!href) return;
    try {
      const url = new URL(href, hostWindow.location.href);
      if (url.origin !== hostWindow.location.origin) return;
      if (/\/pages\/compare-tickets\/?$/.test(url.pathname)) capture('compare_tickets_opened');
      const ticket = ticketName(target.dataset.passId || target.closest('[data-ticket]')?.dataset.ticket)
        || ({ 'general-ticket-2027': 'conference', 'vip-ticket-2027': 'vip', 'mastermind-ticket-2027': 'mastermind' })[url.pathname.split('/').pop()];
      if (ticket && /\/products\//.test(url.pathname)) capture('ticket_selected', { ticket_type: ticket });
    } catch { /* Invalid or external links are not analytics input. */ }
  });
  doc.addEventListener('submit', event => {
    if (event.target?.id === 'mm-cart-form' && event.submitter?.name === 'checkout' && !event.submitter.disabled) capture('checkout_clicked');
  });
  try {
    if (hostWindow.Shopify?.customerPrivacy?.analyticsProcessingAllowed) syncConsent();
    else hostWindow.Shopify?.loadFeatures?.([{ name: 'consent-tracking-api', version: '0.1' }], error => { if (!error) syncConsent(); });
  } catch { /* Consent unavailable: fail closed without changing the storefront. */ }
  return api;
}

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  try {
    const element = document.querySelector('[data-mm-posthog-config]');
    if (element) setupPostHog(JSON.parse(element.textContent));
  } catch { /* Invalid settings must never affect shopping. */ }
}
